import { NavLink, Link, useLocation } from "react-router-dom";
import { useEffect, useState } from "react";
import { Menu, X, Phone, Mail, MapPin, Clock } from "lucide-react";
import { CONTACT_INFO } from "@/lib/data";

const LOGO_URL = "https://customer-assets-m6fa6gv7.emergentagent.net/job_lea-parentalite/artifacts/twod6frq_WhatsApp%20Image%202026-07-19%20at%2020.53.47.jpeg";

const NAV_ITEMS = [
  { to: "/", label: "Accueil", testId: "nav-home" },
  { to: "/a-propos", label: "À propos", testId: "nav-about" },
  { to: "/services", label: "Services & Tarifs", testId: "nav-services" },
  { to: "/blog", label: "Blog", testId: "nav-blog" },
  { to: "/rendez-vous", label: "Rendez-vous", testId: "nav-booking" },
  { to: "/contact", label: "Contact", testId: "nav-contact" },
];

function Header() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();
  const isHome = location.pathname === "/";
  const onDarkHero = isHome && !scrolled;

  useEffect(() => { setOpen(false); }, [location.pathname]);
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-300 ${
        scrolled
          ? "bg-[#FAF7F2]/85 backdrop-blur-xl border-b border-[#E5DCD6]"
          : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-10 h-20 flex items-center justify-between">
        <Link to="/" data-testid="brand-link" className="flex items-center gap-3 group">
          <span className={`inline-flex w-11 h-11 rounded-full overflow-hidden bg-[#FAF7F2] items-center justify-center shrink-0 transition-all duration-300 group-hover:scale-105 ${onDarkHero ? "ring-2 ring-[#FAF7F2]/70 shadow-lg" : "ring-1 ring-[#E5DCD6]"}`}>
            <img src={LOGO_URL} alt="Léa Parentalité — logo" className="w-full h-full object-cover" />
          </span>
          <span className={`font-serif text-2xl transition-colors duration-300 ${onDarkHero ? "text-[#FAF7F2] drop-shadow-[0_1px_8px_rgba(0,0,0,0.45)]" : "text-[#4A403A]"}`}>Léa Parentalité</span>
        </Link>

        <nav className="hidden lg:flex items-center gap-9">
          {NAV_ITEMS.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.to === "/"}
              data-testid={item.testId}
              className={({ isActive }) =>
                `text-sm tracking-wide link-underline transition-colors duration-300 ${
                  onDarkHero
                    ? (isActive ? "text-[#FAF7F2] font-medium" : "text-[#FAF7F2]/85")
                    : (isActive ? "text-[#4A403A] font-medium" : "text-[#8B7E74]")
                }`
              }
            >
              {item.label}
            </NavLink>
          ))}
          <Link
            to="/rendez-vous"
            data-testid="header-cta-booking"
            className="ml-2 rounded-full px-5 py-2.5 bg-[#4A403A] text-[#FAF7F2] text-sm tracking-wide transition-all duration-300 hover:-translate-y-0.5 hover:bg-[#3a322d]"
          >
            Prendre rendez-vous
          </Link>
        </nav>

        <button
          data-testid="mobile-menu-toggle"
          onClick={() => setOpen((v) => !v)}
          className={`lg:hidden w-10 h-10 rounded-full border flex items-center justify-center transition-colors ${onDarkHero ? "border-[#FAF7F2]/50 text-[#FAF7F2]" : "border-[#E5DCD6] text-[#4A403A]"}`}
          aria-label="Menu"
        >
          {open ? <X size={18} /> : <Menu size={18} />}
        </button>
      </div>

      {open && (
        <div data-testid="mobile-menu" className="lg:hidden bg-[#FAF7F2] border-t border-[#E5DCD6]">
          <div className="px-6 py-6 flex flex-col gap-4">
            {NAV_ITEMS.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.to === "/"}
                data-testid={`mobile-${item.testId}`}
                className={({ isActive }) =>
                  `text-base ${isActive ? "text-[#4A403A] font-medium" : "text-[#8B7E74]"}`
                }
              >
                {item.label}
              </NavLink>
            ))}
            <Link
              to="/rendez-vous"
              data-testid="mobile-header-cta-booking"
              className="mt-2 rounded-full px-5 py-3 bg-[#4A403A] text-[#FAF7F2] text-sm tracking-wide text-center"
            >
              Prendre rendez-vous
            </Link>
          </div>
        </div>
      )}
    </header>
  );
}

function Footer() {
  return (
    <footer data-testid="site-footer" className="bg-[#4A403A] text-[#FAF7F2] mt-32">
      <div className="max-w-7xl mx-auto px-6 lg:px-10 pt-20 pb-10">
        <div className="grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <p className="text-xs tracking-[0.2em] uppercase text-[#D2A69A] mb-4">Parlons-en</p>
            <h3 className="font-serif text-4xl md:text-5xl text-[#FAF7F2] leading-tight mb-6">
              Une question, un doute,<br/>un besoin d'écoute&nbsp;?
            </h3>
            <Link
              to="/rendez-vous"
              data-testid="footer-cta-booking"
              className="inline-flex items-center gap-2 rounded-full px-7 py-3 bg-[#D2A69A] text-[#4A403A] text-sm tracking-wide transition-all duration-300 hover:-translate-y-0.5 hover:bg-[#FAF7F2]"
            >
              Prendre rendez-vous →
            </Link>
          </div>

          <div className="lg:col-span-4 space-y-4">
            <p className="text-xs tracking-[0.2em] uppercase text-[#D2A69A]">Coordonnées</p>
            <a href={`tel:${CONTACT_INFO.phoneRaw}`} data-testid="footer-phone" className="flex items-center gap-3 text-[#FAF7F2]/90 hover:text-[#FAF7F2]">
              <Phone size={15} strokeWidth={1.5} /> {CONTACT_INFO.phone}
            </a>
            <a href={`mailto:${CONTACT_INFO.email}`} data-testid="footer-email" className="flex items-center gap-3 text-[#FAF7F2]/90 hover:text-[#FAF7F2] break-all">
              <Mail size={15} strokeWidth={1.5} /> {CONTACT_INFO.email}
            </a>
            <p className="flex items-center gap-3 text-[#FAF7F2]/90">
              <MapPin size={15} strokeWidth={1.5} /> {CONTACT_INFO.zone}
            </p>
            <p className="flex items-center gap-3 text-[#FAF7F2]/90">
              <Clock size={15} strokeWidth={1.5} /> {CONTACT_INFO.hours}
            </p>
          </div>

          <div className="lg:col-span-3 space-y-3">
            <p className="text-xs tracking-[0.2em] uppercase text-[#D2A69A]">Navigation</p>
            {NAV_ITEMS.map((item) => (
              <Link
                key={item.to}
                to={item.to}
                data-testid={`footer-${item.testId}`}
                className="block text-sm text-[#FAF7F2]/80 hover:text-[#FAF7F2]"
              >
                {item.label}
              </Link>
            ))}
          </div>
        </div>

        <div className="mt-16 pt-8 border-t border-[#FAF7F2]/15 flex flex-col md:flex-row justify-between gap-4 text-xs text-[#FAF7F2]/55">
          <p>© {new Date().getFullYear()} Léa Parentalité — Éducatrice de Jeunes Enfants diplômée d'État</p>
          <p>Conseil parental à domicile · Dijon & Côte-d'Or</p>
        </div>
      </div>
    </footer>
  );
}

export default function Layout({ children }) {
  return (
    <div className="min-h-screen bg-[#FAF7F2] text-[#4A403A]">
      <Header />
      <main className="pt-20">{children}</main>
      <Footer />
    </div>
  );
}
